import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getDateString } from "@/lib/family-date";

export interface CalendarEvent {
  id: string;
  child_id: string;
  family_id: string;
  event_type: "school" | "activity" | "deadline" | "notice";
  title: string;
  description: string | null;
  day_of_week: number | null;
  specific_date: string | null;
  start_time: string | null;
  end_time: string | null;
  recurring: boolean;
  source: "manual" | "school_plan_import" | "spond_import";
  source_ref: string | null;
  metadata: Record<string, any>;
  created_by: string | null;
  created_at: string;
  valid_from?: string | null;
  valid_until?: string | null;
}

/** Fetch all calendar events for a child */
export function useChildCalendarEvents(childId: string | undefined) {
  return useQuery({
    queryKey: ["child-calendar-events", childId],
    queryFn: async () => {
      if (!childId) return [];
      const { data, error } = await supabase
        .from("child_calendar_events" as any)
        .select("*")
        .eq("child_id", childId)
        .order("start_time", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as unknown as CalendarEvent[];
    },
    enabled: !!childId,
  });
}

/** Get events for a specific date (recurring by day_of_week + specific_date) */
export function useCalendarEventsForDate(
  childId: string | undefined,
  date: Date,
  timezone: string
) {
  const dateStr = getDateString(date, timezone);
  // 0=Mon..6=Sun (ISO weekday - 1)
  const dayOfWeek = ((date.getDay() + 6) % 7);

  return useQuery({
    queryKey: ["child-calendar-events-date", childId, dateStr],
    queryFn: async () => {
      if (!childId) return [];
      const { data, error } = await supabase
        .from("child_calendar_events" as any)
        .select("*")
        .eq("child_id", childId)
        .or(
          `and(recurring.eq.true,day_of_week.eq.${dayOfWeek},or(valid_from.is.null,valid_from.lte.${dateStr}),or(valid_until.is.null,valid_until.gte.${dateStr})),and(recurring.eq.false,specific_date.eq.${dateStr})`
        )
        .order("start_time", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as unknown as CalendarEvent[];
    },
    enabled: !!childId,
  });
}

/** CRUD mutations */
export function useCalendarEventMutations(childId: string | undefined) {
  const qc = useQueryClient();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["child-calendar-events", childId] });
    qc.invalidateQueries({ queryKey: ["child-calendar-events-date", childId] });
  };

  const addEvent = useMutation({
    mutationFn: async (event: Omit<CalendarEvent, "id" | "created_at">) => {
      const { error } = await supabase
        .from("child_calendar_events" as any)
        .insert(event as any);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const updateEvent = useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & Partial<CalendarEvent>) => {
      const { error } = await supabase
        .from("child_calendar_events" as any)
        .update(updates as any)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const deleteEvent = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("child_calendar_events" as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return { addEvent, updateEvent, deleteEvent };
}
